import { formatInTimeZone } from "date-fns-tz";
import { parseDateOnlyAsUTC } from "@common/helpers/date";
import { fromMonetaryToInt } from "@common/helpers/monetary";
import {
    ScheduleSourceType,
    ScheduleType,
} from "@common/types/domains/accounting/schedule";

export interface AccruedRecognitionPeriod {
    effectiveDate: string;
    amount: number;
}

export interface AccruedBilling {
    billedDateOnly: string;
    amount: number;
}

export interface AccruedBillingViolation {
    amount: number;
    /** `YYYY-MM` */
    firstMonth: string;
    /** `YYYY-MM` */
    lastMonth: string;
}

export type AccruedBillingNoun = "Invoice" | "Bill" | "Payment" | "Amount";

/** The document that bills an accrued schedule; manual billings name the amount. */
export const ACCRUED_BILLING_NOUN_BY_SOURCE: Record<
    ScheduleSourceType,
    AccruedBillingNoun
> = {
    [ScheduleSourceType.invoice]: "Invoice",
    [ScheduleSourceType.bill]: "Bill",
    [ScheduleSourceType.transaction]: "Payment",
    [ScheduleSourceType.manual]: "Amount",
};

const VIOLATION_VERB_BY_NOUN: Record<AccruedBillingNoun, string> = {
    Invoice: "covers",
    Bill: "covers",
    Payment: "covers",
    Amount: "includes",
};

interface AccruedBillingCopy {
    notYetRecognized: string;
    ahead: string;
    policy: string;
    recognized: string;
}

const ACCRUED_EXPENSE_BILLING_COPY: AccruedBillingCopy = {
    notYetRecognized: "expense not yet incurred",
    ahead: "a prepaid expense",
    policy: "prepaid",
    recognized: "Expense",
};

/** Fixed assets never accrue; they share the expense side's wording. */
const ACCRUED_BILLING_COPY_BY_TYPE: Record<ScheduleType, AccruedBillingCopy> = {
    [ScheduleType.revenue]: {
        notYetRecognized: "revenue not yet earned",
        ahead: "deferred revenue",
        policy: "deferred",
        recognized: "Revenue",
    },
    [ScheduleType.expense]: ACCRUED_EXPENSE_BILLING_COPY,
    [ScheduleType.fixedAsset]: ACCRUED_EXPENSE_BILLING_COPY,
};

export function toMonthKey(dateOnly: string): string {
    return dateOnly.slice(0, 7);
}

function sumCentsByMonth(
    entries: Array<{ dateOnly: string; amount: number }>,
): Map<string, number> {
    const centsByMonth = new Map<string, number>();
    for (const entry of entries) {
        const monthKey = toMonthKey(entry.dateOnly);
        centsByMonth.set(
            monthKey,
            (centsByMonth.get(monthKey) ?? 0) + fromMonetaryToInt(entry.amount),
        );
    }
    return centsByMonth;
}

/**
 * The accrued cumulative rule: at every month-end, an accrued schedule's
 * cumulative billed amount must not exceed its cumulative recognized amount.
 * Billing that runs ahead is deferred revenue. The largest month-end excess
 * is reported with the later recognition months that would earn it.
 */
export function findAccruedBillingViolation(options: {
    periods: AccruedRecognitionPeriod[];
    billings: AccruedBilling[];
}): AccruedBillingViolation | null {
    const recognizedByMonth = sumCentsByMonth(
        options.periods.map((period) => ({
            dateOnly: period.effectiveDate,
            amount: period.amount,
        })),
    );
    const billedByMonth = sumCentsByMonth(
        options.billings.map((billing) => ({
            dateOnly: billing.billedDateOnly,
            amount: billing.amount,
        })),
    );
    const monthKeys = [
        ...new Set([...recognizedByMonth.keys(), ...billedByMonth.keys()]),
    ].sort();

    let cumulativeExcessCents = 0;
    let monthIndex = -1;
    let excessCents = 0;
    for (const [index, monthKey] of monthKeys.entries()) {
        cumulativeExcessCents +=
            (billedByMonth.get(monthKey) ?? 0) -
            (recognizedByMonth.get(monthKey) ?? 0);
        if (cumulativeExcessCents > excessCents) {
            monthIndex = index;
            excessCents = cumulativeExcessCents;
        }
    }
    if (monthIndex === -1) {
        return null;
    }
    const coveringMonthKeys = monthKeys
        .slice(monthIndex + 1)
        .filter((monthKey) => (recognizedByMonth.get(monthKey) ?? 0) > 0);
    let coveredCents = 0;
    const lastCoveringIndex = coveringMonthKeys.findIndex((monthKey) => {
        coveredCents += recognizedByMonth.get(monthKey) ?? 0;
        return coveredCents >= excessCents;
    });
    const violatingMonthKey = monthKeys[monthIndex];
    return {
        amount: excessCents / 100,
        firstMonth: coveringMonthKeys[0] ?? violatingMonthKey,
        lastMonth:
            coveringMonthKeys[
                lastCoveringIndex === -1
                    ? coveringMonthKeys.length - 1
                    : lastCoveringIndex
            ] ?? violatingMonthKey,
    };
}

function formatMonth(monthKey: string, pattern: string): string {
    return formatInTimeZone(
        parseDateOnlyAsUTC(`${monthKey}-01`),
        "UTC",
        pattern,
    );
}

/** `Apr–Jun 2026`, `Nov 2025–Feb 2026`, or `Mar 2026` for a single month. */
export function formatMonthRange(options: {
    firstMonth: string;
    lastMonth: string;
}): string {
    const { firstMonth, lastMonth } = options;
    if (firstMonth === lastMonth) {
        return formatMonth(firstMonth, "MMM yyyy");
    }
    const isSameYear = firstMonth.slice(0, 4) === lastMonth.slice(0, 4);
    return `${formatMonth(firstMonth, isSameYear ? "MMM" : "MMM yyyy")}–${formatMonth(lastMonth, "MMM yyyy")}`;
}

export function formatAccruedAmount(amount: number): string {
    const fractionDigits = Number.isInteger(amount) ? 0 : 2;
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    }).format(amount);
}

export function buildAccruedBillingViolationMessage(options: {
    noun: AccruedBillingNoun;
    violation: AccruedBillingViolation;
    scheduleType: ScheduleType;
}): string {
    const { noun, violation } = options;
    const copy = ACCRUED_BILLING_COPY_BY_TYPE[options.scheduleType];
    return `${noun} ${VIOLATION_VERB_BY_NOUN[noun]} ${copy.notYetRecognized}. ${formatAccruedAmount(violation.amount)} relates to ${formatMonthRange(violation)}, which is ${copy.ahead}. Apply a ${copy.policy} policy for those months.`;
}

/** `lockedMonths` are `YYYY-MM` keys of the locked recognition months. */
export function buildAccruedLockedStartMessage(options: {
    lockedMonths: string[];
    scheduleType: ScheduleType;
}): string {
    const sortedMonths = [...options.lockedMonths].sort();
    const firstMonth = sortedMonths[0];
    const lastMonth = sortedMonths[sortedMonths.length - 1];
    const { recognized } = ACCRUED_BILLING_COPY_BY_TYPE[options.scheduleType];
    return `Start date is in a locked period. ${recognized} would be recognized in ${formatMonthRange({ firstMonth, lastMonth })}. Unlock the books through ${formatMonth(lastMonth, "MMM yyyy")} or choose a later start date.`;
}
